import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSession } from "@/lib/auth-client";
import {
  DEFAULT_DEMAND_FILTERS,
  DEMAND_FILTER_KEYS,
  type DemandFilterState,
  decodeDemandFilters,
  encodeDemandFilters,
} from "@/lib/demand-filters";

// Per user, so two people sharing a browser don't inherit each other's view (spec A3).
const storageKey = (userId: string) => `nos.demands.filters.${userId}`;

function readStorage(userId: string): Record<string, string> | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    // Keep string values only — decodeDemandFilters validates them.
    return Object.fromEntries(
      Object.entries(parsed).filter((e): e is [string, string] => typeof e[1] === "string"),
    );
  } catch {
    return null;
  }
}

function writeStorage(userId: string, encoded: Record<string, string>): void {
  try {
    if (Object.keys(encoded).length === 0) localStorage.removeItem(storageKey(userId));
    else localStorage.setItem(storageKey(userId), JSON.stringify(encoded));
  } catch {
    // localStorage unavailable (private mode) — the URL remains the source of truth.
  }
}

/**
 * Demands page search, filters and expanded rows, held in the URL and remembered per user
 * in localStorage. A bare /dashboard/demands visit restores the last saved view once the
 * session is known.
 */
export function useDemandFilters(): {
  state: DemandFilterState;
  setState: (next: DemandFilterState) => void;
  reset: () => void;
} {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate({ from: "/dashboard/demands" });
  const userId = useSession().data?.user.id ?? null;
  const hydratedRef = useRef(false);

  const urlHasFilters = DEMAND_FILTER_KEYS.some((k) => typeof search[k] === "string");
  const state = useMemo(() => decodeDemandFilters(search), [search]);

  const setState = useCallback(
    (next: DemandFilterState) => {
      const encoded = encodeDemandFilters(next);
      if (userId !== null) writeStorage(userId, encoded);
      navigate({ search: () => encoded, replace: true });
    },
    [navigate, userId],
  );

  // Clearing filters keeps the expanded rows — "Clear all" is about the list, not the view.
  const reset = useCallback(
    () => setState({ ...DEFAULT_DEMAND_FILTERS, open: state.open }),
    [setState, state.open],
  );

  useEffect(() => {
    if (hydratedRef.current || userId === null) return;
    hydratedRef.current = true;
    if (urlHasFilters) return;
    const stored = readStorage(userId);
    if (stored && Object.keys(stored).length > 0) setState(decodeDemandFilters(stored));
  }, [urlHasFilters, setState, userId]);

  return { state, setState, reset };
}
